import { Close, Edit, Save } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { differenceInSeconds, set } from "date-fns";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";

import { GqlPlannerVisitStatus } from "~/__generated__";
import { StatusCheckmark } from "~/modules/app/components/StatusCheckmark";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDate, formatSeconds, formatTime } from "~/modules/app/util/formatters";
import { ReviewVisitTableCell } from "~/modules/project/screens/ProjectReviewVisitV2Screen/components/ReviewVisitTable";
import UserBadge from "~/modules/user/components/UserBadge";

import { TimeReportTableRowFragment } from "./fragments.generated";

interface TimeReportTableRowProps {
    report: TimeReportTableRowFragment;
    visitStatus: GqlPlannerVisitStatus;
    onUpdate: (reportId: string, values: TimeReportValues) => void;
}

export type TimeReportValues = {
    startedAt: Date | null;
    stoppedAt: Date | null;
};

type TimeReportFormValues = {
    startedAtDate: Date | null;
    startedAtTime: Date | null;
    stoppedAtDate: Date | null;
    stoppedAtTime: Date | null;
};

type OpenField = "startedAtTime" | "startedAtDate" | "stoppedAtTime" | "stoppedAtDate" | null;

export function TimeReportTableRow({ report, visitStatus, onUpdate }: TimeReportTableRowProps) {
    const { t } = useTranslation("project");
    const [isEditing, setIsEditing] = useState(false);
    const [openField, setOpenField] = useState<OpenField>(null);

    const startedAt = report.startedAt ? parseStringToDate(report.startedAt) : null;
    const stoppedAt = report.stoppedAt ? parseStringToDate(report.stoppedAt) : null;

    const { control, handleSubmit, reset } = useForm<TimeReportFormValues>({
        defaultValues: {
            startedAtDate: startedAt,
            startedAtTime: startedAt,
            stoppedAtDate: stoppedAt,
            stoppedAtTime: stoppedAt,
        },
    });

    const handleEditClick = () => {
        reset({
            startedAtDate: startedAt,
            startedAtTime: startedAt,
            stoppedAtDate: stoppedAt,
            stoppedAtTime: stoppedAt,
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        reset();
        setIsEditing(false);
    };

    const handleFormSubmit = (values: TimeReportFormValues) => {
        const merge = (date: Date | null, time: Date | null): Date | null => {
            if (!date || !time) return null;
            return set(date, {
                hours: time.getHours(),
                minutes: time.getMinutes(),
                seconds: time.getSeconds(),
            });
        };

        onUpdate(report.id, {
            startedAt: merge(values.startedAtDate, values.startedAtTime),
            stoppedAt: merge(values.stoppedAtDate, values.stoppedAtTime),
        });

        setIsEditing(false);
    };

    return (
        <>
            <ReviewVisitTableCell direction="row" gap={1} alignItems="center">
                <StatusCheckmark checked={report.hasValidTimeRange} />
                {report.user && (
                    <UserBadge
                        names={[report.user.firstName, report.user.lastName]}
                        profilePicture={report.user.profilePictureMedia?.url}
                        size={25}
                        showName
                        linkTo={generatePath("/admin/users/:userId", { userId: report.user.id })}
                    />
                )}
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                {isEditing ? (
                    <Stack gap={0.5}>
                        <Controller
                            name="startedAtTime"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TimePicker
                                    {...field}
                                    format="HH:mm"
                                    timeSteps={{ minutes: 1 }}
                                    open={openField === "startedAtTime"}
                                    onOpen={() => setOpenField("startedAtTime")}
                                    onClose={() => setOpenField(null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            error: !!fieldState.error,
                                            onClick: () => setOpenField("startedAtTime"),
                                            sx: {
                                                width: 80,
                                                "& input": { cursor: "pointer", textAlign: "center", p: 0.5 },
                                            },
                                        },
                                        openPickerButton: { sx: { display: "none" } },
                                        inputAdornment: { sx: { display: "none" } },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="startedAtDate"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DatePicker
                                    {...field}
                                    format="dd-MM"
                                    open={openField === "startedAtDate"}
                                    onOpen={() => setOpenField("startedAtDate")}
                                    onClose={() => setOpenField(null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            error: !!fieldState.error,
                                            helperText: fieldState.error?.message,
                                            onClick: () => setOpenField("startedAtDate"),
                                            sx: {
                                                "& input": { cursor: "pointer", textAlign: "center", p: 0.5 },
                                            },
                                        },
                                        openPickerButton: { sx: { display: "none" } },
                                        inputAdornment: { sx: { display: "none" } },
                                    }}
                                />
                            )}
                        />
                    </Stack>
                ) : (
                    <>
                        <Typography variant="body2" fontWeight="bold">
                            {startedAt ? formatTime(startedAt) : "—"}
                        </Typography>
                        <Typography variant="body2">{startedAt ? formatDate(startedAt) : "—"}</Typography>
                    </>
                )}
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                {isEditing ? (
                    <Stack gap={0.5}>
                        <Controller
                            name="stoppedAtTime"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TimePicker
                                    {...field}
                                    format="HH:mm"
                                    timeSteps={{ minutes: 1 }}
                                    open={openField === "stoppedAtTime"}
                                    onOpen={() => setOpenField("stoppedAtTime")}
                                    onClose={() => setOpenField(null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            error: !!fieldState.error,
                                            onClick: () => setOpenField("stoppedAtTime"),
                                            sx: {
                                                width: 80,
                                                "& input": { cursor: "pointer", textAlign: "center", p: 0.5 },
                                            },
                                        },
                                        openPickerButton: { sx: { display: "none" } },
                                        inputAdornment: { sx: { display: "none" } },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="stoppedAtDate"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DatePicker
                                    {...field}
                                    format="dd-MM"
                                    open={openField === "stoppedAtDate"}
                                    onOpen={() => setOpenField("stoppedAtDate")}
                                    onClose={() => setOpenField(null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            error: !!fieldState.error,
                                            helperText: fieldState.error?.message,
                                            onClick: () => setOpenField("stoppedAtDate"),
                                            sx: {
                                                "& input": { cursor: "pointer", textAlign: "center", p: 0.5 },
                                            },
                                        },
                                        openPickerButton: { sx: { display: "none" } },
                                        inputAdornment: { sx: { display: "none" } },
                                    }}
                                />
                            )}
                        />
                    </Stack>
                ) : stoppedAt ? (
                    <>
                        <Typography variant="body2" fontWeight="bold">
                            {formatTime(stoppedAt)}
                        </Typography>
                        <Typography variant="body2">{formatDate(stoppedAt)}</Typography>
                    </>
                ) : (
                    <Typography variant="body2" fontWeight="bold">
                        —
                    </Typography>
                )}
            </ReviewVisitTableCell>

            <ReviewVisitTableCell>
                <Typography variant="body2">
                    {report.startedAt && report.stoppedAt
                        ? formatSeconds(differenceInSeconds(report.stoppedAt, report.startedAt))
                        : "—"}
                </Typography>
            </ReviewVisitTableCell>
            <ReviewVisitTableCell>
                {visitStatus !== GqlPlannerVisitStatus.Approved &&
                    (isEditing ? (
                        <Stack direction="column">
                            <IconButton size="small" color="primary" onClick={handleSubmit(handleFormSubmit)}>
                                <Save />
                            </IconButton>

                            <IconButton size="small" onClick={handleCancel}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Stack>
                    ) : (
                        <IconButton size="small" onClick={handleEditClick}>
                            <Edit fontSize="small" />
                        </IconButton>
                    ))}
            </ReviewVisitTableCell>

            {report.outOfProtocolNotes !== "" && (
                <ReviewVisitTableCell sx={{ gridColumn: "1/ -1", paddingTop: 0 }}>
                    <Typography variant="caption">{t("projectVisitWorkerReport.labelOutOfProtocolNotes")}</Typography>
                    <Typography variant="body2">{report.outOfProtocolNotes}</Typography>
                </ReviewVisitTableCell>
            )}
        </>
    );
}
